import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BaseDestroy } from 'nera-common/angular';
import {
    Component,
    inject,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
    ActivatedRoute,
    ActivatedRouteSnapshot,
    NavigationEnd,
    Router,
    RouterOutlet,
} from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { isSystemOrg } from 'app/core/constants/system-org.constants';
import {
    FEATURE_FLAGS,
    FeatureFlagService,
} from 'app/core/services/feature-flag.service';
import { OrgContextService } from 'app/core/services/org-context.service';
import { MyOrganizationsService } from 'app/core/services/my-organizations.service';
import { WarningLevel } from 'app/core/models/quota-usage';
import { QuotaStore } from 'app/core/services/quota-store.service';
import { QuotaNoticeService } from 'app/core/services/quota-notice.service';
import { TenantThemeService } from 'app/core/services/tenant-theme.service';
import { TenantContextService } from 'app/core/services/tenant-context.service';
import { ConnectionStatusComponent } from 'app/layout/common/connection-status/connection-status.component';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { OrgPickerComponent } from 'app/layout/common/org-picker/org-picker.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { UsageBannerComponent } from 'app/shared/components/usage-banner/usage-banner.component';
import { QuotaBannerComponent } from 'app/shared/components/quota-banner/quota-banner.component';
import { ADMIN_TIER_ROLES, ROLES } from 'app/shared/constants';
import { AbsoluteMediaUrlPipe } from 'app/shared/pipes/absolute-media-url.pipe';
import { QuotaExceededInfo } from 'app/shared/utils/quota.utils';
import { combineLatest, filter, startWith, Subscription } from 'rxjs';
import { IUser } from '../../../../core/models/auth.model';
import { AuthService } from '../../../../core/services/auth.service';
import { AuthorizationService } from '../../../../core/services/authorization.service';

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [
        ConnectionStatusComponent,
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        UserComponent,
        MatIconModule,
        MatButtonModule,
        LanguagesComponent,
        NotificationsComponent,
        OrgPickerComponent,
        RouterOutlet,
        UsageBannerComponent,
        QuotaBannerComponent,
        TranslocoPipe,
        AbsoluteMediaUrlPipe,
    ],
})
export class ClassyLayoutComponent extends BaseDestroy implements OnInit {
    protected readonly _featureFlags = inject(FeatureFlagService);
    protected readonly FEATURE_FLAGS = FEATURE_FLAGS;
    private readonly _activatedRoute = inject(ActivatedRoute);
    private readonly _router = inject(Router);
    private readonly _navigationService = inject(NavigationService);
    private readonly _authService = inject(AuthService);
    private readonly _authorizationService = inject(AuthorizationService);
    private readonly _fuseMediaWatcherService = inject(FuseMediaWatcherService);
    private readonly _fuseNavigationService = inject(FuseNavigationService);
    private readonly _quotaStore = inject(QuotaStore);
    private readonly _orgContextService = inject(OrgContextService);
    private readonly _quotaNotices = inject(QuotaNoticeService);
    private readonly _myOrganizationsService = inject(MyOrganizationsService);

    /** Tenant-branded logo (T3.10) — org logo on a confirmed tenant host, platform default everywhere else. */
    protected readonly _tenantThemeService = inject(TenantThemeService);
    private readonly _tenantContextService = inject(TenantContextService);

    isScreenSmall: boolean;
    navigation: Navigation;
    user: IUser;

    /** Quota usage state for the usage banner. */
    warningLevel: WarningLevel = 'NONE';
    albumsUsed = 0;
    albumsLimit = 0;
    photosUsed = 0;
    photosLimit = 0;

    /** D2: last quota denial (429/503 from QuotaCheckMiddleware), if any. */
    quotaDenial: QuotaExceededInfo | null = null;
    /** D3: quota-service unreachable — requests are being allowed through unmeasured. */
    quotaDegraded = false;

    /**
     * Org named in the sidebar header under the product name. Resolved exactly
     * like OrgPickerComponent: memberships × `actingOrgId` (the token's org),
     * never the hostname org — those differ on the root host.
     */
    sidebarOrgName = '';
    /**
     * Membership-row logo shown next to the platform mark in the sidebar/topbar
     * header. Read off the same acting-org membership as `sidebarOrgName` —
     * `GET /me/organizations` already carries `orgLogoUrl` per row. Null on
     * SystemOrg (the platform org is the platform mark itself) or when the
     * row has no logo — the platform mark stands alone there.
     */
    sidebarOrgLogoUrl: string | null = null;

    private _quotaSub: Subscription | null = null;

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to the user service
        this._authService.user$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((user: IUser) => {
                this.user = user;
            });

        // Sidebar header names the org the session is acting in; re-resolve
        // when either the membership list or the acting org arrives.
        combineLatest([
            this._myOrganizationsService.getMyOrganizations(),
            this._tenantContextService.context$,
        ])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(([memberships, ctx]) => {
                const actingId = ctx.actingOrgId ?? ctx.orgId;
                const acting = memberships.find((m) => m.orgId === actingId);
                this.sidebarOrgName = acting?.orgName ?? '';
                // System org shows as PhotoFlow itself (platform mark); its own
                // row logo would render "PhotoFlow next to PhotoFlow".
                const logo = acting?.orgLogoUrl?.trim() || null;
                this.sidebarOrgLogoUrl = isSystemOrg(actingId) ? null : logo;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });

        // D2/D3: quota verdicts arrive on ANY request, so the banners live in the
        // shell rather than in whichever feature happened to make the call.
        this._quotaNotices.denial$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((denial) => {
                this.quotaDenial = denial;
            });

        this._quotaNotices.degraded$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((degraded) => {
                this.quotaDegraded = degraded;
            });

        // PFL-1680: quota usage is only relevant on Overview/quota/billing pages
        // (route data `showQuotaBanner`) — fetching it on every route, including
        // DAM/storage/invitations, was a wasted GET /quotas/usage/{orgId} per page.
        this._router.events
            .pipe(
                filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                startWith(null),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
                // D2: the denial describes the action the user just attempted, so it
                // must not follow them onto the next page.
                this._quotaNotices.clearDenial();
                if (this._routeWantsQuotaBanner()) this._loadQuotaUsage();
                else this._stopQuotaUsage();
            });
    }

    /** Walks the full route snapshot chain — `data` isn't auto-inherited by lazy children. */
    private _routeWantsQuotaBanner(): boolean {
        let route: ActivatedRouteSnapshot | null =
            this._activatedRoute.snapshot.root;
        while (route) {
            if (route.data?.['showQuotaBanner']) return true;
            route = route.firstChild;
        }
        return false;
    }

    /**
     * On destroy
     */

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    /**
     * Left nav for any catalog role. `ADMIN_TIER_ROLES` covers
     * SUPER_ADMIN/PLATFORM_ADMIN/ORG_OWNER/ORG_OPERATOR.
     */
    showSideNav(): boolean {
        return (
            !!this.user &&
            this._authorizationService.hasAnyRole([
                ...ADMIN_TIER_ROLES,
                ROLES.PHOTOGRAPHER,
                ROLES.EDITOR,
                ROLES.USER,
            ])
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Follow the shared quota store while the route shows the banner. The store caches the
     * single `GET users/me/quota` and re-emits when a quota/subscription SignalR event
     * invalidates it, so the banner no longer refetches per navigation or per event here.
     * Silently fails — the banner simply won't show if the API is unavailable or the user
     * has no org context.
     */
    private _loadQuotaUsage(): void {
        // Only meaningful with an org context; the endpoint reads the org off the token.
        if (this._quotaSub || !this._orgContextService.orgId) {
            return;
        }

        this._quotaSub = this._quotaStore.usage$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((usage) => {
                this.warningLevel = usage.warningLevel;
                this.albumsUsed = usage.albumsUsed;
                this.albumsLimit = usage.albumsLimit;
                this.photosUsed = usage.photosUsed;
                this.photosLimit = usage.photosLimit;

                // HARD renders as a red banner; the pages owning a create action disable it too.
            });
    }

    /** Off a banner route: stop following the store (so quota events do not refetch there). */
    private _stopQuotaUsage(): void {
        this._quotaSub?.unsubscribe();
        this._quotaSub = null;
        this.warningLevel = 'NONE';
    }
}
